/**
 * Iter-228w — Bank Accounts admin page.
 *
 * Lets the CEO manage the BAIM bank accounts surfaced on every freshly
 * generated invoice PDF:
 *   • MAD scope: one account with titulaire / banque / domiciliation / RIB / SWIFT
 *   • DEVISE scope: one row per foreign currency correspondent
 *     (EUR via CAIXA, USD via BNYM, …). A "via correspondant" toggle
 *     replaces the SWIFT line on the PDF by the routed-via phrase.
 *
 * Resolution at invoice render time:
 *   1. mission.bank_account_id override → use that account
 *   2. invoice currency == MAD → MAD default
 *   3. else → DEVISE account whose currency matches the invoice (default fallback)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Star, ExternalLink, Save, X, RefreshCw, ScanLine, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SettingsHeader } from "./_shared";

const EMPTY = {
  scope: "MAD",
  label: "",
  is_default: false,
  titulaire: "", banque: "", domiciliation: "", rib: "", swift: "",
  beneficiary: "", iban: "", currency: "",
  via_correspondent: false,
  correspondent_bank_name: "", correspondent_swift: "",
};

export default function BankAccountsSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // doc or {scope}/null

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bank-accounts");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const madRows = useMemo(() => items.filter((b) => b.scope === "MAD"), [items]);
  const deviseRows = useMemo(() => items.filter((b) => b.scope === "DEVISE"), [items]);

  const onSave = async (doc) => {
    try {
      if (doc.bank_account_id) {
        await api.put(`/admin/bank-accounts/${doc.bank_account_id}`, doc);
      } else {
        await api.post(`/admin/bank-accounts`, doc);
      }
      toast.success("Enregistré");
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const onDelete = async (row) => {
    if (!confirm(`Supprimer "${row.label}" ?`)) return;
    try {
      await api.delete(`/admin/bank-accounts/${row.bank_account_id}`);
      toast.success("Supprimé");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const onSetDefault = async (row) => {
    try {
      await api.post(`/admin/bank-accounts/${row.bank_account_id}/set-default`);
      toast.success(`"${row.label}" défini comme défaut pour le scope ${row.scope}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  // Iter-228x — read-only details popup opened by clicking a row.
  const [viewing, setViewing] = useState(null);

  return (
    <div className="space-y-6 p-6 max-w-5xl" data-testid="bank-accounts-page">
      <SettingsHeader
        kicker="Billing"
        title="Coordonnées bancaires"
        description="Comptes bancaires affichés sur chaque facture générée. Le compte est choisi automatiquement selon la devise de la facture, et peut être surchargé au niveau de chaque mission."
      />
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {/* Iter-228w-bis — OCR import (upload a RIB / statement → Gemini extract → pre-fill dialog). */}
        <ImportFromOcrButton onParsed={(payload) => setEditing(payload)} />
        {/* Iter-228w-bis — bulk regen for unpaid drifted invoices */}
        <BulkRegenButton />
      </div>

      <ScopeSection
        title="Compte MAD (Dirhams)"
        helper="Utilisé sur toute facture dont la devise est MAD."
        rows={madRows}
        loading={loading}
        scope="MAD"
        onAdd={() => setEditing({ ...EMPTY, scope: "MAD" })}
        onEdit={(r) => setEditing(r)}
        onDelete={onDelete}
        onSetDefault={onSetDefault}
        onView={(r) => setViewing(r)}
      />

      <ScopeSection
        title="Comptes Devises étrangères"
        helper="Un compte par devise (EUR, USD, GBP, …). Le compte avec la devise correspondante est choisi en priorité ; à défaut, le défaut global du scope DEVISE est utilisé."
        rows={deviseRows}
        loading={loading}
        scope="DEVISE"
        onAdd={() => setEditing({ ...EMPTY, scope: "DEVISE" })}
        onEdit={(r) => setEditing(r)}
        onDelete={onDelete}
        onSetDefault={onSetDefault}
        onView={(r) => setViewing(r)}
      />

      {editing && (
        <BankAccountDialog
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      )}

      {viewing && (
        <BankAccountDetailsDialog
          row={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
        />
      )}
    </div>
  );
}


function ScopeSection({ title, helper, rows, loading, scope, onAdd, onEdit, onDelete, onSetDefault, onView }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-medium">{title}</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{helper}</p>
        </div>
        <Button size="sm" onClick={onAdd} data-testid={`bank-add-${scope.toLowerCase()}`}>
          <Plus size={14} /> Ajouter
        </Button>
      </div>
      <div className="divide-y divide-zinc-100">
        {loading ? (
          <div className="px-4 py-6 text-sm text-zinc-500">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-zinc-500 italic">Aucun compte dans ce scope.</div>
        ) : (
          rows.map((b) => (
            <div key={b.bank_account_id} className="px-4 py-3 grid grid-cols-[1fr_auto] gap-3 items-center cursor-pointer hover:bg-zinc-50/60 transition-colors"
                 onClick={() => onView(b)}
                 data-testid={`bank-row-${b.bank_account_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{b.label || "(sans nom)"}</span>
                  {b.is_default && (
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900">
                      <Star size={9} className="inline -mt-px mr-0.5" /> Défaut
                    </span>
                  )}
                  {b.currency && (
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                      {b.currency}
                    </span>
                  )}
                  {b.via_correspondent && (
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-900">
                      Via correspondant
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono text-zinc-500 mt-1 truncate">
                  {/* Iter-228x — Address (domiciliation) intentionally dropped
                      from the row summary. Full details are accessible by
                      clicking the row → details popup. */}
                  {b.scope === "MAD"
                    ? `${b.titulaire || "—"} · ${b.banque || "—"} · RIB ${b.rib || "—"}`
                    : `${b.beneficiary || "—"} · ${b.banque || "—"} · IBAN ${b.iban || "—"} · SWIFT ${
                        b.via_correspondent ? (b.correspondent_swift || "—") : (b.swift || "—")
                      }`}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {!b.is_default && (
                  <Button size="sm" variant="ghost" onClick={() => onSetDefault(b)}
                    data-testid={`bank-set-default-${b.bank_account_id}`}
                    title="Définir comme défaut pour ce scope">
                    <Star size={12} />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => onEdit(b)}
                  data-testid={`bank-edit-${b.bank_account_id}`}>
                  <Pencil size={12} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(b)}
                  data-testid={`bank-delete-${b.bank_account_id}`}
                  className="text-red-600 hover:text-red-700">
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function _DetailRow({ label, value, mono = false, testId = "" }) {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-2 py-1.5">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 pt-0.5">{label}</div>
      <div className={`text-sm text-zinc-900 break-words ${mono ? "font-mono text-[12px]" : ""}`}
           data-testid={testId || undefined}>
        {value || <span className="text-zinc-400">—</span>}
      </div>
    </div>
  );
}

function BankAccountDetailsDialog({ row, onClose, onEdit }) {
  const isMad = row.scope === "MAD";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="bank-account-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{row.label || "(sans nom)"}</span>
            {row.is_default && (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900">
                <Star size={9} className="inline -mt-px mr-0.5" /> Défaut
              </span>
            )}
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
              {isMad ? "MAD" : (row.currency || "Devise")}
            </span>
            {row.via_correspondent && (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-900">
                Via correspondant
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="divide-y divide-zinc-100">
          {isMad ? (
            <>
              <_DetailRow label="Titulaire" value={row.titulaire} testId="details-titulaire" />
              <_DetailRow label="Banque" value={row.banque} />
              <_DetailRow label="Domiciliation" value={row.domiciliation} />
              <_DetailRow label="RIB" value={row.rib} mono testId="details-rib" />
              <_DetailRow label="SWIFT" value={row.swift} mono />
            </>
          ) : (
            <>
              <_DetailRow label="Bénéficiaire" value={row.beneficiary} testId="details-beneficiary" />
              <_DetailRow label="Devise" value={row.currency} mono />
              <_DetailRow label="Banque" value={row.banque} />
              <_DetailRow label="Domiciliation" value={row.domiciliation} testId="details-domiciliation" />
              <_DetailRow label="IBAN" value={row.iban} mono testId="details-iban" />
              <_DetailRow label="SWIFT BAIM" value={row.swift} mono />
              {row.via_correspondent && (
                <>
                  <_DetailRow label="Banque correspondante" value={row.correspondent_bank_name} testId="details-correspondent-bank" />
                  <_DetailRow label="SWIFT correspondant" value={row.correspondent_swift} mono testId="details-correspondent-swift" />
                </>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="details-close">Fermer</Button>
          <Button onClick={onEdit} data-testid="details-edit">
            <Pencil size={14} /> Éditer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BankAccountDialog({ initial, onCancel, onSave }) {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const isMad = d.scope === "MAD";
  // Iter-228w-ter — list of MAD fields required when scope=MAD and DEVISE
  // fields required when scope=DEVISE. Used to surface a friendly "missing
  // from OCR" hint right under the scope toggle.
  const madRequired = ["titulaire", "banque", "domiciliation", "rib", "swift"];
  const deviseRequired = ["beneficiary", "banque", "domiciliation", "iban", "swift", "currency"];
  const missing = (isMad ? madRequired : deviseRequired).filter((k) => !(d[k] || "").toString().trim());
  const submit = () => {
    if (!d.label?.trim()) { toast.error("Le libellé est requis"); return; }
    onSave(d);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="bank-account-dialog">
        <DialogHeader>
          <DialogTitle>
            {d.bank_account_id ? "Modifier" : "Créer"} — Compte {isMad ? "MAD" : "Devise"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Iter-228w-ter — Scope chooser. The OCR pre-selects MAD/DEVISE
              based on the document content, but the CEO can flip it here.
              Flipping the scope keeps the fields already entered (they're
              just hidden / shown depending on scope). */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Type de compte</Label>
            <div className="mt-1 inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="bank-scope-toggle">
              <button
                type="button"
                onClick={() => set("scope", "MAD")}
                data-testid="bank-scope-mad"
                className={`px-3 h-9 text-xs font-medium transition-colors ${isMad ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                MAD (Dirhams)
              </button>
              <button
                type="button"
                onClick={() => set("scope", "DEVISE")}
                data-testid="bank-scope-devise"
                className={`px-3 h-9 text-xs font-medium border-l border-zinc-200 transition-colors ${!isMad ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                Devise étrangère
              </button>
            </div>
            {missing.length > 0 && (
              <p className="mt-2 text-[11px] text-amber-700" data-testid="bank-missing-hint">
                Champs non détectés par l&apos;OCR, à compléter : <strong>{missing.join(", ")}</strong>
              </p>
            )}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Libellé (interne)</Label>
            <Input value={d.label} onChange={(e) => set("label", e.target.value)}
                   placeholder="ex. CFG Bank — Compte MAD (Agence 2 Mars)"
                   data-testid="bank-field-label" />
          </div>

          {isMad ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Titulaire" value={d.titulaire} onChange={(v) => set("titulaire", v)} testId="bank-field-titulaire" />
                <Field label="Banque" value={d.banque} onChange={(v) => set("banque", v)} testId="bank-field-banque" />
                <Field label="Domiciliation (agence)" value={d.domiciliation} onChange={(v) => set("domiciliation", v)} testId="bank-field-domiciliation" />
                <Field label="RIB" value={d.rib} onChange={(v) => set("rib", v)} testId="bank-field-rib" />
                <Field label="SWIFT / BIC" value={d.swift} onChange={(v) => set("swift", v)} testId="bank-field-swift" />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bénéficiaire" value={d.beneficiary} onChange={(v) => set("beneficiary", v)} testId="bank-field-beneficiary" />
                <Field label="Devise (EUR/USD/GBP/…)" value={d.currency} onChange={(v) => set("currency", v.toUpperCase())} testId="bank-field-currency" />
                <Field label="Banque" value={d.banque} onChange={(v) => set("banque", v)} testId="bank-field-banque-devise" />
                <Field label="Domiciliation (agence)" value={d.domiciliation} onChange={(v) => set("domiciliation", v)} testId="bank-field-domiciliation-devise" />
                <Field label="IBAN" value={d.iban} onChange={(v) => set("iban", v)} testId="bank-field-iban" />
                <Field label="SWIFT BAIM" value={d.swift} onChange={(v) => set("swift", v)} testId="bank-field-swift-devise" />
              </div>

              <div className="bg-zinc-50 border border-zinc-200 rounded p-3 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={d.via_correspondent}
                    onChange={(e) => set("via_correspondent", e.target.checked)}
                    className="mt-0.5"
                    data-testid="bank-field-via-correspondent"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Via correspondant SWIFT (MT103)</div>
                    <div className="text-[11px] text-zinc-500">
                      Affiche sur la facture, après le SWIFT BAIM ci-dessus : « Pour tout virement depuis l&apos;étranger en {d.currency || "{{devise}}"} via une banque correspondante, prière d&apos;utiliser un paiement SWIFT de type MT103 via notre correspondant {d.correspondent_bank_name || "{{Nom Banque}}"}, code SWIFT {d.correspondent_swift || "{{code swift}}"}. ».
                    </div>
                  </div>
                </label>
                {d.via_correspondent && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Nom banque correspondante" value={d.correspondent_bank_name} onChange={(v) => set("correspondent_bank_name", v)} testId="bank-field-correspondent-name" />
                    <Field label="SWIFT correspondant" value={d.correspondent_swift} onChange={(v) => set("correspondent_swift", v)} testId="bank-field-correspondent-swift" />
                  </div>
                )}
              </div>
            </>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={d.is_default}
              onChange={(e) => set("is_default", e.target.checked)}
              data-testid="bank-field-is-default"
            />
            <span className="text-sm">Compte par défaut pour le scope <strong>{d.scope}</strong></span>
          </label>
        </div>
        <DialogFooter className="border-t border-zinc-100 pt-4">
          <Button variant="outline" onClick={onCancel}><X size={12} /> Annuler</Button>
          <Button onClick={submit} data-testid="bank-save"><Save size={12} /> Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, value, onChange, testId, disabled, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testId} disabled={disabled} />
      {hint && <p className="text-[10px] text-zinc-500 italic mt-1">{hint}</p>}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Iter-228w-bis — Bulk regen of every unpaid, non-cancelled invoice whose
// snapshotted bank account differs from the currently-resolved account.
// Used after editing a default account, or flipping a per-mission override,
// to ensure all in-flight invoices reflect the new RIB before they're sent.
// ──────────────────────────────────────────────────────────────────────────
function BulkRegenButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!confirm(
      "Régénérer les PDFs de toutes les factures non payées dont les coordonnées bancaires ont changé ?\n\n" +
      "Les factures payées ou annulées ne seront pas modifiées."
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/invoices/regenerate-unpaid-bank-drifted");
      const n = r.data?.regenerated || 0;
      const sk = r.data?.skipped || 0;
      if (n === 0) {
        toast.info("Aucune facture non payée n'a un compte bancaire désynchronisé.");
      } else {
        toast.success(`${n} facture(s) régénérée(s)${sk > 0 ? ` · ${sk} ignorée(s)` : ""}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid="bank-bulk-regen-unpaid"
      title="Régénère le PDF de toute facture non payée dont le compte bancaire snapshot diffère du compte résolu actuellement."
      className="inline-flex items-center gap-1.5 px-3 h-9 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-900 text-xs rounded transition-colors disabled:opacity-50 shrink-0"
    >
      <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
      {busy ? "Régénération…" : "Régénérer les factures non payées"}
    </button>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Iter-228w-bis — OCR import: upload a scanned RIB / bank statement (PDF or
// image), call the backend Gemini extractor and pre-fill the create dialog
// with the parsed fields. The CEO reviews/edits before saving — nothing is
// persisted by the scan endpoint itself.
// ──────────────────────────────────────────────────────────────────────────
function ImportFromOcrButton({ onParsed }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/bank-accounts/scan-document", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const parsed = r.data || {};
      const conf = typeof parsed.confidence === "number" ? parsed.confidence : null;
      toast.success(
        `Document analysé${conf !== null ? ` · confiance ${Math.round(conf * 100)}%` : ""}. Vérifiez puis enregistrez.`
      );
      // Pre-fill the create dialog with the parsed payload, defaulting any
      // missing field to its EMPTY counterpart so React inputs stay controlled.
      onParsed({ ...EMPTY, ...parsed });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'OCR");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={onFile}
        data-testid="bank-ocr-file-input"
      />
      <button
        onClick={onPick}
        disabled={busy}
        data-testid="bank-ocr-import"
        title="Téléverser un RIB ou un relevé bancaire (PDF/image) — pré-remplit le formulaire via OCR Gemini."
        className="inline-flex items-center gap-1.5 px-3 h-9 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-900 text-xs rounded transition-colors disabled:opacity-50 shrink-0"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ScanLine size={12} />}
        {busy ? "Analyse OCR…" : "Importer depuis un document (OCR)"}
      </button>
    </>
  );
}
